import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, FileText, BookOpen, Music, Scale, Shield, Bot, DollarSign, EyeOff, MessageSquare } from 'lucide-react';
import SEO from './SEO';
import MyCleanStudioHeader from './MyCleanStudioHeader';
import MyCleanStudioFooter from './MyCleanStudioFooter';
import './ArticlesPage.css';

const articles = [
  {
    id: 1,
    title: 'YouTube Demonetization Guide',
    description: 'Understand why videos get demonetized and learn proven strategies to prevent it from happening to your content.',
    icon: AlertTriangle,
    link: '/guides/youtube-demonetization',
    category: 'YouTube'
  },
  {
    id: 2,
    title: 'YouTube Community Guidelines',
    description: 'Complete guide to YouTube\'s community guidelines. Learn what content is allowed and how to stay compliant.',
    icon: FileText,
    link: '/guides/youtube-guidelines',
    category: 'YouTube'
  },
  {
    id: 3,
    title: 'TikTok Content Guidelines',
    description: 'Stay compliant with TikTok\'s content policies and community standards to grow your audience safely.',
    icon: BookOpen,
    link: '/guides/tiktok-guidelines',
    category: 'TikTok'
  },
  {
    id: 4,
    title: 'Copyright & Music Detection',
    description: 'Avoid copyright strikes with our comprehensive guide to music licensing and detection systems.',
    icon: Music,
    link: '/guides/copyright-music-detection',
    category: 'Copyright'
  },
  {
    id: 5,
    title: 'How to Appeal Strikes',
    description: 'Learn the step-by-step process to successfully appeal content strikes and restore your videos.',
    icon: Scale,
    link: '/guides/appeal-strikes',
    category: 'Appeals'
  },
  {
    id: 6,
    title: 'Video Compliance Checker',
    description: 'How our AI-powered compliance checker analyzes your content and keeps you safe from violations.',
    icon: Shield,
    link: '/features/video-compliance-checker',
    category: 'Features'
  },
  {
    id: 7,
    title: 'AI-Generated Content Guidelines',
    description: 'Understand disclosure requirements and rules for AI content on YouTube, TikTok, and Instagram.',
    icon: Bot,
    link: '/guides/ai-content-guidelines',
    category: 'Trending'
  },
  {
    id: 8,
    title: 'Sponsored Content & FTC Rules',
    description: 'Master FTC disclosure requirements for brand deals, sponsorships, and affiliate marketing.',
    icon: DollarSign,
    link: '/guides/sponsored-content-ftc',
    category: 'Legal'
  },
  {
    id: 9,
    title: 'Shadowban Recovery Guide',
    description: 'Detect, understand, and recover from shadowbans on TikTok, Instagram, and YouTube.',
    icon: EyeOff,
    link: '/guides/shadowban-recovery',
    category: 'Recovery'
  },
  {
    id: 10,
    title: 'YouTube Advertiser-Friendly Guidelines',
    description: 'Learn how YouTube\'s Advertiser-Friendly Content Guidelines affect your ad revenue — and how to protect your earnings before you upload.',
    icon: DollarSign,
    link: '/guides/youtube-ad-revenue-compliance',
    category: 'YouTube'
  },
  {
    id: 11,
    title: 'TikTok FYF Eligibility Guide',
    description: 'Understand TikTok\'s For You Feed standards, what triggers reach suppression, and how to keep your content FYF-eligible.',
    icon: AlertTriangle,
    link: '/guides/tiktok-fyf-eligibility',
    category: 'TikTok'
  },
  {
    id: 12,
    title: 'Can You Swear on YouTube?',
    description: 'The 2026 profanity rules for monetized creators: where swearing is fine, and where one word in a title or thumbnail costs you your ads.',
    icon: MessageSquare,
    link: '/guides/youtube-profanity-rules',
    category: 'YouTube'
  }
];

function ArticlesPage() {
  return (
    <>
      <SEO
        title="Creator Guides & Articles - myCleanStudio"
        description="Learn how to keep your content compliant with our comprehensive guides on YouTube, TikTok, copyright, and more."
        keywords="YouTube guides, TikTok guidelines, content compliance, copyright music, appeal strikes, creator resources"
        url="/articles"
      />
      <div className="articles-page">
        {/* Header */}
        <MyCleanStudioHeader />
        <div className="articles-container">

        {/* Hero */}
        <section className="articles-hero">
          <p className="articles-label">RESOURCES</p>
          <h2 className="articles-title">Creator Guides & Articles</h2>
          <p className="articles-subtitle">
            Learn how to keep your content compliant and maximize your reach across platforms.
          </p>
        </section>

        {/* Articles Grid */}
        <section className="articles-grid-section">
          <div className="articles-grid">
            {articles.map((article) => {
              const IconComponent = article.icon;
              return (
                <Link to={article.link} className="article-card" key={article.id}>
                  <div className="article-card-header">
                    <div className="article-icon">
                      <IconComponent size={28} />
                    </div>
                    <span className="article-category">{article.category}</span>
                  </div>
                  <h3 className="article-title">{article.title}</h3>
                  <p className="article-description">{article.description}</p>
                  <span className="article-read-more">Read article →</span>
                </Link>
              );
            })}
            </div>
          </section>
        </div>

        {/* Footer */}
        <MyCleanStudioFooter />
      </div>
    </>
  );
}

export default ArticlesPage;
